import React from 'react'

import { Box, ModalBody, ModalClose, ModalFooter, ModalHeader } from '@gousto-internal/citrus-react'
import { useDispatch, useSelector } from 'react-redux'

import type { RecipeQueryResponse } from '@library/api-menu-experience'

import type { RecipeCoreId, RecipeUUID } from 'reducers/menu-recipes/recipes'
import { menuRecipeDetailVisibilityChange } from 'routes/Menu/actions/menuRecipeDetails'
import { AddRecipeButton } from 'routes/Menu/components/Detail/AddRecipeButton'
import { RecipeDescription } from 'routes/Menu/components/Detail/Description'
import { DetailError } from 'routes/Menu/components/Detail/DetailError'
import { RecipeImage } from 'routes/Menu/components/Detail/Image'
import { SectionPanel } from 'routes/Menu/components/Detail/SectionPanel'
import { Title } from 'routes/Menu/components/Detail/Title'
import { getNumPortions } from 'selectors/basket'

import { RecipeAllergens } from './Allergens/RecipeAllergens'
import { RecipeAttributes } from './Attributes/RecipeAttributes'
import { RecipeQuickStats } from './Attributes/RecipeQuickStats'
import { RecipeEquipment } from './Equipment/RecipeEquipment'
import { RecipeIngredients } from './Ingredients/RecipeIngredients'
import { RecipeNutrition } from './Nutrition/RecipeNutrition'
import { RecipeOptions } from './Options/RecipeOptions'

type DetailContentProps = {
  uuid: RecipeUUID
  coreId: RecipeCoreId
  title: string
  error: boolean
  loading: boolean
  recipe: RecipeQueryResponse | null
  position?: string
  reload: () => void
  onClose: () => void
  onSelectRecipe?: (recipeId: RecipeUUID) => void
}

export const DetailContent = ({
  uuid,
  coreId,
  title,
  error,
  loading,
  recipe,
  reload,
  onClose,
  onSelectRecipe,
  position,
}: DetailContentProps) => {
  const numPortions = useSelector(getNumPortions)
  const dispatch = useDispatch()

  return (
    <>
      <ModalClose onClose={onClose} />
      <ModalHeader>
        <Title data-testing="recipeDetailTitle">{title}</Title>
      </ModalHeader>
      <ModalBody>
        <Box>
          <RecipeImage images={{ experience: recipe?.images }} title={title} />
          <SectionPanel>
            <RecipeOptions
              customisations={recipe?.customisations}
              selectedRecipeId={uuid}
              onSelectRecipe={
                onSelectRecipe ||
                ((recipeId) => dispatch(menuRecipeDetailVisibilityChange(recipeId)))
              }
            />
            <RecipeQuickStats tagGroups={recipe?.tagGroups} />
            <RecipeDescription
              description={recipe?.description}
              packagingDescriptionHTML={recipe?.packagingDescriptionHTML}
              loading={loading}
            />
            <RecipeAttributes tagGroups={recipe?.tagGroups} loading={loading} />
          </SectionPanel>
          {error ? (
            <DetailError reload={reload} />
          ) : (
            <>
              <RecipeIngredients ingredients={recipe?.ingredients} loading={loading} />
              <RecipeEquipment equipment={recipe?.equipment} loading={loading} onClose={onClose} />
              <RecipeNutrition
                nutrition={recipe?.nutrition}
                numPortions={numPortions}
                tagGroups={recipe?.tagGroups}
                loading={loading}
              />
              <RecipeAllergens
                ingredients={recipe?.ingredients}
                allergens={recipe?.allergens}
                loading={loading}
              />
            </>
          )}
        </Box>
      </ModalBody>
      {!error && (
        <ModalFooter>
          <AddRecipeButton
            coreId={coreId}
            id={uuid}
            surchargeLabel={recipe?.pricing?.perPortionSurcharge}
            isNegativeSurcharge={Boolean(recipe?.pricing?.isNegative)}
            position={position}
          />
        </ModalFooter>
      )}
    </>
  )
}
